import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Settings, 
  X, 
  RefreshCw, 
  Users, 
  Shield, 
  Bug,
  ChevronDown,
  ChevronRight,
  Check
} from 'lucide-react';
import { 
  MOCK_PROFILES, 
  getActiveMockProfile, 
  enableMockProfile, 
  disableMockProfile,
  isMockProfileEnabled,
  MockProfile
} from '../utils/mockProfiles';

// Environment detection
const isLocalhost = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || 
         hostname === '127.0.0.1' || 
         hostname.startsWith('192.168.') ||
         hostname.endsWith('.local');
};

// Check if we're on UAT (not prod)
const isUATEnvironment = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname.toLowerCase();
  // UAT hostnames - NOT production (askaldo.unifirst.com)
  return hostname === 'askaldo-uat.unifirst.com' || // Official UAT URL
         hostname.includes('lemon-ocean') || // Azure Static Web Apps UAT
         hostname.includes('azurestaticapps.net'); // Any Azure Static Web App (dev/UAT)
};

// Check if we're on production
const isProductionEnvironment = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname.toLowerCase();
  // Production is specifically askaldo.unifirst.com
  return hostname === 'askaldo.unifirst.com';
};

// Check if dev testing panel should be available (localhost OR UAT, but NOT prod)
const isDevTestingAvailable = (): boolean => {
  return isLocalhost() || (isUATEnvironment() && !isProductionEnvironment());
};

// Super admin roles that can access dev testing panel (strict - only true super-admins)
const SUPER_ADMIN_ROLES = ['super-admin', 'askaldo-admin'];

// Custom user roles for testing
interface CustomUserRoles {
  // General roles
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isDeveloper: boolean;
  // Agent-specific admin roles
  isHrAdmin: boolean;
  isPwmsAdmin: boolean;
  isProductAdmin: boolean;
  isAldoAdmin: boolean;
  isOwensboroAdmin: boolean;
  isUnfDocsAdmin: boolean;
  isOrientationAdmin: boolean;
  isIrisAdmin: boolean;
  isServiceDeskAdmin: boolean;
  isDirectEmbroideryAdmin: boolean;
  // Other settings
  department: string;
  location: string;
  customAgentAccess: string[];
}

const DEFAULT_USER_ROLES: CustomUserRoles = {
  isSuperAdmin: false,
  isAdmin: false,
  isDeveloper: false,
  isHrAdmin: false,
  isPwmsAdmin: false,
  isProductAdmin: false,
  isAldoAdmin: false,
  isOwensboroAdmin: false,
  isUnfDocsAdmin: false,
  isOrientationAdmin: false,
  isIrisAdmin: false,
  isServiceDeskAdmin: false,
  isDirectEmbroideryAdmin: false,
  department: '',
  location: '',
  customAgentAccess: []
};

// Agent admin role mappings for the UI
const AGENT_ADMIN_ROLES = [
  { key: 'isSuperAdmin', label: 'Super Admin', role: 'super-admin', color: 'text-red-600' },
  { key: 'isAdmin', label: 'Admin', role: 'admin', color: 'text-orange-600' },
  { key: 'isHrAdmin', label: 'HR Policy Admin', role: 'hr-admin', color: 'text-purple-600' },
  { key: 'isPwmsAdmin', label: 'PWMS Admin', role: 'pwms-admin', color: 'text-orange-500' },
  { key: 'isProductAdmin', label: 'Product Admin', role: 'product-admin', color: 'text-blue-600' },
  { key: 'isAldoAdmin', label: 'Scout Admin', role: 'aldo-admin', color: 'text-green-600' },
  { key: 'isOwensboroAdmin', label: 'Owensboro Admin', role: 'owensboro2-admin', color: 'text-pink-600' },
  { key: 'isUnfDocsAdmin', label: 'UniFirst Docs Admin', role: 'unfdocs-admin', color: 'text-teal-600' },
  { key: 'isOrientationAdmin', label: 'Orientation Admin', role: 'orientation-admin', color: 'text-cyan-600' },
  { key: 'isIrisAdmin', label: 'IRIS Admin', role: 'iris-admin', color: 'text-slate-600' },
  { key: 'isServiceDeskAdmin', label: 'Service Desk Admin', role: 'service-desk-admin', color: 'text-fuchsia-600' },
  { key: 'isDirectEmbroideryAdmin', label: 'Direct Embroidery Admin', role: 'direct-embroidery-admin', color: 'text-orange-600' },
  { key: 'isDeveloper', label: 'Developer', role: 'developer', color: 'text-gray-600' },
] as const;

// LocalStorage key for custom roles
const CUSTOM_ROLES_KEY = 'askaldo_dev_custom_roles';
const CUSTOM_ROLES_ENABLED_KEY = 'askaldo_dev_custom_roles_enabled';

const getCustomRoles = (): CustomUserRoles | null => {
  try {
    if (localStorage.getItem(CUSTOM_ROLES_ENABLED_KEY) !== 'true') return null;
    const stored = localStorage.getItem(CUSTOM_ROLES_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const saveCustomRoles = (roles: CustomUserRoles): void => {
  localStorage.setItem(CUSTOM_ROLES_KEY, JSON.stringify(roles));
  localStorage.setItem(CUSTOM_ROLES_ENABLED_KEY, 'true');
};

const clearCustomRoles = (): void => {
  localStorage.removeItem(CUSTOM_ROLES_KEY);
  localStorage.removeItem(CUSTOM_ROLES_ENABLED_KEY);
};

// Available agents for custom access
const ALL_AGENTS = [
  { id: 'aldo', name: 'Scout' },
  { id: 'hrpolicy', name: 'PolicyTech' },
  { id: 'orientation', name: 'Orientation' },
  { id: 'product', name: 'Product Searchlight' },
  { id: 'pwms-support', name: 'PWMS Support' },
  { id: 'direct_embroidery', name: 'Direct Embroidery' },
  { id: 'unifirst_docs', name: 'UniFirst Docs' },
  { id: 'iris', name: 'IRIS' },
  { id: 'owensboro2', name: 'Owensboro Production' },
  { id: 'service-desk', name: 'Service Desk' }
];

interface DevTestingPanelProps {
  onRefresh?: () => void;
  userRoles?: string[];
}

export const DevTestingPanel: React.FC<DevTestingPanelProps> = ({ onRefresh, userRoles = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeProfile, setActiveProfile] = useState<MockProfile | null>(null);
  const [customRoles, setCustomRoles] = useState<CustomUserRoles>(DEFAULT_USER_ROLES);
  const [customRolesEnabled, setCustomRolesEnabled] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    profiles: true,
    roles: false,
    agents: false
  });

  // Check if user has super-admin access
  const hasSuperAdminAccess = useMemo(() => {
    // On localhost, always allow (for development)
    if (isLocalhost()) return true;
    // On UAT, require super-admin role
    return userRoles.some(role => SUPER_ADMIN_ROLES.includes(role.toLowerCase()));
  }, [userRoles]);

  // Load state on mount
  useEffect(() => {
    if (!isDevTestingAvailable() || !hasSuperAdminAccess) return;
    setActiveProfile(getActiveMockProfile());
    const roles = getCustomRoles();
    if (roles) {
      setCustomRoles(roles);
      setCustomRolesEnabled(true);
    }
  }, [hasSuperAdminAccess]);

  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const handleProfileSelect = useCallback((profileId: string) => {
    if (activeProfile?.id === profileId) {
      disableMockProfile();
      setActiveProfile(null);
    } else {
      enableMockProfile(profileId);
      setActiveProfile(MOCK_PROFILES[profileId]);
    }
  }, [activeProfile]);

  const handleDisableProfile = useCallback(() => {
    disableMockProfile();
    setActiveProfile(null);
  }, []);

  const handleRoleChange = useCallback((key: keyof CustomUserRoles, value: any) => {
    const newRoles = { ...customRoles, [key]: value };
    setCustomRoles(newRoles);
    // Auto-enable custom roles when any role is changed
    if (!customRolesEnabled) {
      setCustomRolesEnabled(true);
    }
    saveCustomRoles(newRoles);
  }, [customRoles, customRolesEnabled]);

  const handleToggleAgent = useCallback((agentId: string) => {
    const currentAccess = customRoles.customAgentAccess || [];
    const newAccess = currentAccess.includes(agentId)
      ? currentAccess.filter(id => id !== agentId)
      : [...currentAccess, agentId];
    handleRoleChange('customAgentAccess', newAccess);
  }, [customRoles.customAgentAccess, handleRoleChange]);

  const handleEnableCustomRoles = useCallback((enabled: boolean) => {
    setCustomRolesEnabled(enabled);
    if (enabled) {
      saveCustomRoles(customRoles);
    } else {
      clearCustomRoles();
    }
  }, [customRoles]);

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  // Don't render if not available or user doesn't have access - must be after all hooks
  if (!isDevTestingAvailable() || !hasSuperAdminAccess) {
    return null;
  }

  // Determine environment label for UI
  const envLabel = isLocalhost() ? 'localhost' : 'UAT only';

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 w-12 h-12 bg-purple-600 hover:bg-purple-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
        title="Developer Testing Panel"
      >
        <Bug className="w-6 h-6" />
      </button>

      {/* Panel overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div className="relative bg-white dark:bg-gray-900 w-full sm:w-[420px] max-h-[80vh] sm:max-h-[600px] rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-purple-600 text-white">
              <div className="flex items-center gap-2">
                <Bug className="w-5 h-5" />
                <h2 className="font-semibold">Dev Testing Panel</h2>
                <span className="text-xs bg-purple-500 px-2 py-0.5 rounded-full">{envLabel}</span>
              </div>
              <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-purple-500 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Active Status Banner */}
              {(activeProfile || customRolesEnabled) && (
                <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
                    <Shield className="w-4 h-4" />
                    <span className="font-medium text-sm">Testing Mode Active</span>
                  </div>
                  {activeProfile && (
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                      Profile: {activeProfile.name}
                    </p>
                  )}
                  {customRolesEnabled && (
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                      Custom roles enabled
                    </p>
                  )}
                </div>
              )}

              {/* Mock Profiles Section */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('profiles')}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750"
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-600" />
                    <span className="font-medium">Mock Profiles</span>
                  </div>
                  {expandedSections.profiles ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>

                {expandedSections.profiles && (
                  <div className="p-3 space-y-2">
                    {Object.values(MOCK_PROFILES).map(profile => (
                      <button
                        key={profile.id}
                        onClick={() => handleProfileSelect(profile.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg border transition-all ${
                          activeProfile?.id === profile.id
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30'
                            : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{profile.name}</span>
                          {activeProfile?.id === profile.id && (
                            <Check className="w-4 h-4 text-purple-600" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {profile.description}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          Roles: {profile.roles.filter(r => r !== 'authenticated').join(', ') || '(defaults only)'}
                        </p>
                      </button>
                    ))}

                    {activeProfile && (
                      <button
                        onClick={handleDisableProfile}
                        className="w-full text-center text-sm text-red-600 hover:text-red-700 py-2"
                      >
                        Disable Mock Profile
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Custom Roles Section */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('roles')}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750"
                >
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-600" />
                    <span className="font-medium">User Role Overrides</span>
                  </div>
                  {expandedSections.roles ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>

                {expandedSections.roles && (
                  <div className="p-3 space-y-3">
                    {/* Enable toggle */}
                    <label className="flex items-center justify-between">
                      <span className="text-sm font-medium">Enable Custom Roles</span>
                      <input
                        type="checkbox"
                        checked={customRolesEnabled}
                        onChange={(e) => handleEnableCustomRoles(e.target.checked)}
                        className="w-4 h-4 accent-purple-600"
                      />
                    </label>

                    <hr className="border-gray-200 dark:border-gray-700" />

                    {/* Quick presets */}
                    <div className="flex flex-wrap gap-1">
                      <button
                        onClick={() => {
                          // Enable all roles
                          const allEnabled = { ...DEFAULT_USER_ROLES };
                          AGENT_ADMIN_ROLES.forEach(r => {
                            (allEnabled as any)[r.key] = true;
                          });
                          setCustomRoles(allEnabled);
                          if (customRolesEnabled) saveCustomRoles(allEnabled);
                        }}
                        disabled={!customRolesEnabled}
                        className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50"
                      >
                        All On
                      </button>
                      <button
                        onClick={() => {
                          setCustomRoles(DEFAULT_USER_ROLES);
                          if (customRolesEnabled) saveCustomRoles(DEFAULT_USER_ROLES);
                        }}
                        disabled={!customRolesEnabled}
                        className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                      >
                        All Off
                      </button>
                      <button
                        onClick={() => {
                          const superOnly = { ...DEFAULT_USER_ROLES, isSuperAdmin: true };
                          setCustomRoles(superOnly);
                          if (customRolesEnabled) saveCustomRoles(superOnly);
                        }}
                        disabled={!customRolesEnabled}
                        className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50"
                      >
                        Super Admin Only
                      </button>
                    </div>

                    <hr className="border-gray-200 dark:border-gray-700" />

                    {/* All admin role toggles */}
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {AGENT_ADMIN_ROLES.map(roleConfig => (
                        <label key={roleConfig.key} className="flex items-center justify-between py-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 px-2 rounded">
                          <span className={`text-sm ${roleConfig.color}`}>{roleConfig.label}</span>
                          <input
                            type="checkbox"
                            checked={(customRoles as any)[roleConfig.key] || false}
                            onChange={(e) => handleRoleChange(roleConfig.key as keyof CustomUserRoles, e.target.checked)}
                            className="w-4 h-4 accent-purple-600 cursor-pointer"
                          />
                        </label>
                      ))}
                    </div>

                    <hr className="border-gray-200 dark:border-gray-700" />

                    <div>
                      <label className="text-sm block mb-1">Department</label>
                      <input
                        type="text"
                        value={customRoles.department}
                        onChange={(e) => handleRoleChange('department', e.target.value)}
                        disabled={!customRolesEnabled}
                        placeholder="e.g., Sales, IT, Production"
                        className="w-full px-3 py-1.5 text-sm border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                      />
                    </div>

                    <div>
                      <label className="text-sm block mb-1">Location</label>
                      <input
                        type="text"
                        value={customRoles.location}
                        onChange={(e) => handleRoleChange('location', e.target.value)}
                        disabled={!customRolesEnabled}
                        placeholder="e.g., Owensboro, HQ"
                        className="w-full px-3 py-1.5 text-sm border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Custom Agent Access Section */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('agents')}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750"
                >
                  <div className="flex items-center gap-2">
                    <Settings className="w-4 h-4 text-green-600" />
                    <span className="font-medium">Custom Agent Access</span>
                  </div>
                  {expandedSections.agents ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>

                {expandedSections.agents && (
                  <div className="p-3 space-y-2">
                    <p className="text-xs text-gray-500 mb-2">
                      Select specific agents to include (requires custom roles enabled)
                    </p>
                    {ALL_AGENTS.map(agent => (
                      <label key={agent.id} className="flex items-center justify-between py-1">
                        <span className="text-sm">{agent.name}</span>
                        <input
                          type="checkbox"
                          checked={customRoles.customAgentAccess?.includes(agent.id) || false}
                          onChange={() => handleToggleAgent(agent.id)}
                          disabled={!customRolesEnabled}
                          className="w-4 h-4 accent-purple-600"
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleRefresh}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh to Apply Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Export helpers for use in other components
export { getCustomRoles, isLocalhost, isUATEnvironment, isProductionEnvironment, isDevTestingAvailable };

export default DevTestingPanel;

